import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Table as AntTable, Button, Input, Modal, message, Space, Switch, Popconfirm } from "antd";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useBranchContext } from "../../../contexts/branch-context";
import { getTables, createTable, updateTable, deleteTable, Table as TableType } from "../../../service/table";
import { StyledSwitch } from "../../../styles/shared-components";
import { colors, spacing } from "../../../styles/theme";

const Wrapper = styled.div`
  padding: ${spacing.md};
  background: ${colors.white};
  border-radius: 8px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.lg};
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: ${colors.textPrimary};
`;

const Description = styled.p`
  color: ${colors.textSecondary};
  margin: 4px 0 0;
  font-size: 14px;
`;

export const PosSettings: React.FC = () => {
    const { t } = useTranslation();
    const { currentBranch } = useBranchContext();
    const queryClient = useQueryClient();
    const [modalVisible, setModalVisible] = useState(false);
    const [editingTable, setEditingTable] = useState<TableType | null>(null);
    const [tableName, setTableName] = useState("");

    const { data: tables, isLoading } = useQuery(
        ["tables", currentBranch?.id],
        () => getTables(currentBranch!.id),
        {
            enabled: !!currentBranch,
        }
    );

    const createMutation = useMutation(createTable, {
        onSuccess: () => {
            message.success(t("posSettings.tableAdded"));
            queryClient.invalidateQueries(["tables"]);
            handleCloseModal();
        },
        onError: () => {
            message.error(t("posSettings.tableAddError"));
        },
    });

    const updateMutation = useMutation(
        (data: { id: number; data: Partial<TableType> }) => updateTable(data.id, data.data),
        {
            onSuccess: () => {
                message.success(t("posSettings.tableUpdated"));
                queryClient.invalidateQueries(["tables"]);
                handleCloseModal();
            },
            onError: () => {
                message.error(t("posSettings.tableUpdateError"));
            },
        }
    );

    const deleteMutation = useMutation(deleteTable, {
        onSuccess: () => {
            message.success(t("posSettings.tableDeleted"));
            queryClient.invalidateQueries(["tables"]);
        },
        onError: () => {
            message.error(t("posSettings.tableDeleteError"));
        },
    });

    const handleOpenModal = (table?: TableType) => {
        if (table) {
            setEditingTable(table);
            setTableName(table.name);
        } else {
            setEditingTable(null);
            setTableName("");
        }
        setModalVisible(true);
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setEditingTable(null);
        setTableName("");
    };

    const handleSubmit = () => {
        if (!tableName.trim()) return;

        if (editingTable) {
            updateMutation.mutate({
                id: editingTable.id,
                data: { name: tableName },
            });
        } else {
            if (!currentBranch) return;
            createMutation.mutate({
                name: tableName,
                companyBranchId: currentBranch.id,
            });
        }
    };

    const columns = [
        {
            title: t("posSettings.columnTableName"),
            dataIndex: "name",
            key: "name",
        },
        {
            title: t("posSettings.columnStatus"),
            key: "isActive",
            render: (_: any, record: TableType) => (
                <StyledSwitch
                    checked={record.isActive}
                    onChange={(checked) =>
                        updateMutation.mutate({ id: record.id, data: { isActive: checked } })
                    }
                />
            ),
        },
        {
            title: t("posSettings.columnActions"),
            key: "actions",
            render: (_: any, record: TableType) => (
                <Space>
                    <Button
                        type="text"
                        icon={<PencilSimple size={18} />}
                        onClick={() => handleOpenModal(record)}
                    />
                    <Popconfirm
                        title={t("posSettings.deleteConfirm")}
                        onConfirm={() => deleteMutation.mutate(record.id)}
                        okText={t("posSettings.yes")}
                        cancelText={t("posSettings.no")}
                    >
                        <Button type="text" danger icon={<Trash size={18} />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Wrapper>
            <Header>
                <div>
                    <Title>{t("posSettings.title")}</Title>
                    <Description>{t("posSettings.description")}</Description>
                </div>
                <Button
                    type="primary"
                    icon={<Plus size={18} />}
                    onClick={() => handleOpenModal()}
                >
                    {t("posSettings.addTable")}
                </Button>
            </Header>

            <AntTable
                columns={columns}
                dataSource={tables}
                rowKey="id"
                loading={isLoading}
                pagination={false}
            />

            <Modal
                title={editingTable ? t("posSettings.editTable") : t("posSettings.addNewTable")}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={handleCloseModal}
                confirmLoading={createMutation.isLoading || updateMutation.isLoading}
            >
                <Input
                    placeholder={t("posSettings.tableNamePlaceholder")}
                    value={tableName}
                    onChange={(e) => setTableName(e.target.value)}
                    onPressEnter={handleSubmit}
                />
            </Modal>
        </Wrapper>
    );
};
